import { createContext, useContext, ReactNode } from "react";

interface GuestBookingValue {
  /**
   * True when the booking wizard is running in the unauthenticated guest flow
   * (`/guest/booking`). The authenticated client portal
   * never wraps the wizard in this provider, so `isGuest` defaults to `false`
   * there and existing behaviour is unchanged.
   */
  isGuest: boolean;
}

const GuestBookingContext = createContext<GuestBookingValue>({ isGuest: false });

/**
 * Marks its subtree as the guest booking flow. The shared wizard (and its data/
 * payment/submit edges) reads `useGuestBooking().isGuest` to branch guest-only
 * behaviour (guest v3 job-data, guest stripe keys, guest create), WITHOUT touching
 * the authenticated path. This is the single seam the guest-only behaviour hangs off.
 */
export const GuestBookingProvider = ({ children }: { children: ReactNode }) => (
  <GuestBookingContext.Provider value={{ isGuest: true }}>{children}</GuestBookingContext.Provider>
);

/**
 * Read the guest-flow flag from anywhere inside the wizard. Returns `{ isGuest: false }`
 * outside a `GuestBookingProvider` (i.e. the authenticated client portal), so callers can
 * be added incrementally without a guard.
 */
export const useGuestBooking = (): GuestBookingValue => useContext(GuestBookingContext);
